import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck,
  Gauge,
  Zap,
  Weight,
  Fuel,
  Settings,
  CircleDot,
  Ruler,
  ChevronDown,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { useAppContext } from "../context/AppContext";

const TruckProductDetail = () => {
  const { slug } = useParams();
  const { getTruckBySlug, loading, error } = useAppContext();
  const [showMore, setShowMore] = useState(false);

  const truck = getTruckBySlug(slug);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-600">Loading truck details...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-screen text-center">
        <p className="text-red-600 text-lg font-semibold">Error: {error}</p>
      </div>
    );

  if (!truck)
    return (
      <div className="flex items-center justify-center min-h-screen text-center">
        <p className="text-gray-600 text-lg">Truck not found</p>
        <p className="text-gray-500">
          The truck with slug "{slug}" does not exist.
        </p>
      </div>
    );

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* --- Hero Section --- */}
      <section className="relative py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          {/* Truck Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex justify-center items-center"
          >
            {truck.image_url ? (
              <img
                src={truck.image_url}
                alt={truck.name}
                className="w-full max-w-2xl object-contain rounded-2xl transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="w-full max-w-md h-96 bg-gray-200 rounded-2xl flex items-center justify-center">
                <span className="text-gray-500">No image available</span>
              </div>
            )}
          </motion.div>

          {/* Truck Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center space-y-6 text-center lg:text-left"
          >

            <h1 className="text-5xl font-bold text-gray-900 leading-tight">
              {truck.name}
            </h1>

            {truck.category === "Buses" && (
              <p className="mt-2 text-sm md:text-base font-medium text-red-600">
                Available as Complete Bus & Bus Chassis
              </p>
            )}

            <p className="text-lg text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
              {truck.small_description}
            </p>

            {/* {truck.price && (
              <div className="bg-red-100 text-red-700 inline-flex items-center justify-center px-3 py-1.5 rounded-full font-semibold shadow-sm w-fit text-sm md:text-base">
                PKR {truck.price}
              </div>
            )} */}

          </motion.div>
        </div>
      </section>

      {/* --- Specifications Section --- */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Key Specifications
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {truck.specifications ? (
              Object.entries(truck.specifications).map(([key, value]) => {
                const specIcons = {
                  model: Truck,
                  wheelbase: Ruler,
                  length: Ruler,
                  engine: Zap,
                  engine_output: Gauge,
                  engine_torque: Weight,
                  gvwr: Weight,
                  fuel_tank: Fuel,
                  tires: CircleDot,
                };
                const Icon = specIcons[key] || Settings;
                const label = key
                  .split("_")
                  .map(
                    (word) => word.charAt(0).toUpperCase() + word.slice(1)
                  )
                  .join(" ");

                return (
                  <div
                    key={key}
                    className="flex flex-col items-start bg-white border border-red-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-semibold text-gray-700">
                        {label}
                      </span>
                    </div>
                    <span className="text-base font-bold text-gray-900 mt-1">
                      {value}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="col-span-full text-center text-gray-600">
                No specifications available
              </p>
            )}
          </div>
        </div>
      </section>

      {/* --- Description Section --- */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <button
            onClick={() => setShowMore(!showMore)}
            className="w-full flex justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-red-500 transition-all"
          >
            <span className="text-lg font-semibold text-gray-800">
              {showMore
                ? "Hide Detailed Description"
                : "View Detailed Description"}
            </span>
            <ChevronDown
              className={`w-6 h-6 text-red-600 transition-transform duration-300 ${showMore ? "rotate-180" : ""
                }`}
            />
          </button>

          <AnimatePresence>
            {showMore && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="overflow-hidden mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
                  {truck.big_description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* --- CTA Section --- */}
      <section className="py-16 bg-red-600 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">
          Interested in the {truck.name}?
        </h2>

        <p className="text-red-100 max-w-2xl mx-auto mb-6">
          Contact Isuzu Lyallpur Motors to book your truck or get detailed
          specifications and delivery options.
        </p>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
          <Link
            to="/contact"
            className="bg-white text-red-600 font-semibold px-8 py-3 rounded-xl shadow-lg hover:bg-red-50 transition-all inline-flex items-center gap-2"
          >
            Request a Quote <ArrowRight className="w-5 h-5" />
          </Link>

          <a
            href={`https://wa.me/923108887382?text=${encodeURIComponent(
              `Hello, I want to know more about Isuzu ${truck.name}.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-3 rounded-xl shadow-lg transition-all inline-flex items-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default TruckProductDetail;
